import { Account } from '@eagle/core-data-types';
import { Button, styled, TableCell, TableRow } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { FC, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { T_ONE } from '../../constants';
import { useFetchAllCache, usePromise } from '../../hooks';
import { CacheDataTypes } from '../../types';
import { filterDeletedCache, testid } from '../../util';
import { BasicTableCard } from '../basic-table-card';
import { ErrorMessage } from '../error-message';
import { FetchOneOfAll } from '../fetch';
import { MiddleSpinner } from '../middle-spinner';
import { ResponsiveText } from '../responsive-text';

interface Props {
  accounts: string[];
  ownerAccountId?: string;
  addAndRemove?: boolean;
  entityType: string;
  onAddAccount?: () => void;
  onRemoveAccount?: (account: Account) => void;
}

const StyledTableRow = styled(TableRow)();
const StyledTableCell = styled(TableCell)();

export const SharingCard: FC<Props> = ({ accounts, addAndRemove, entityType, ownerAccountId, onAddAccount, onRemoveAccount }) => {
  const { t } = useTranslation(['common']);
  const routerLocation = useLocation();
  const theme = useTheme();
  const myAccountsCache = useFetchAllCache(CacheDataTypes.MY_ACCOUNT);

  const titleText = t('common:common.labels.sharing');

  const [accountIds, accountIdsError, accountIdsState] = usePromise(async () => {
    const data = await filterDeletedCache<Account>(myAccountsCache);
    return data.map(({ _id }) => _id);
  }, [myAccountsCache]);

  const filterAccounts = useMemo(() => {
    if (!accountIds) return [];
    return [...new Set(accounts)].filter((accountItem) => accountIds.includes(accountItem));
  }, [accountIds, accounts]);

  const renderTitle = (): JSX.Element => (
    <ResponsiveText
      variant="h5"
      color="text.primary"
      responsiveOptions={{
        minFontSize: '1.25rem',
        maxFontSize: '1.5rem',
        textLengthThreshold: 25,
      }}
    >
      {titleText}
    </ResponsiveText>
  );

  const renderHint = (): JSX.Element => {
    if (!filterAccounts.length) return <>{t('common:common.hint.not-shared', { entityType })}</>;
    return <>{t('common:common.hint.shared', { entityType })}</>;
  };

  const renderCreateAction = (): JSX.Element => {
    if (!onAddAccount || !addAndRemove) return <></>;
    return (
      (() => {
        const AddAccountButton = styled(Button)({ alignSelf: 'flex-end' });
        return (
          <AddAccountButton
            data-testid="add-account-button"
            onClick={onAddAccount}
          >
            {t('common:common.action.add-entity', { entity: t('common:terms.account', { count: T_ONE }) })}
          </AddAccountButton>
        );
      })()
    );
  };

  const renderRemoveButton = (accountItem: Account): JSX.Element => {
    if (!onRemoveAccount || !addAndRemove || ownerAccountId === accountItem._id) return <></>;
    return (
      (() => {
        const RemoveButton = styled(Button)({ color: theme.palette.error.main });
        return (
          <RemoveButton
            data-testid="remove-button"
            onClick={() => onRemoveAccount(accountItem)}
          >
            {t('common:common.action.remove')}
          </RemoveButton>
        );
      })()
    );
  };

  const renderHead = (): JSX.Element => (
    <StyledTableCell component="div">{t('common:terms.account', { count: T_ONE })}</StyledTableCell>
  );

  const renderBody = (account: string, index: number): JSX.Element => (
    <FetchOneOfAll
      id={account}
      key={index}
      dataType={CacheDataTypes.MY_ACCOUNT}
      renderFactory={(accountItem: Account) => (
        <StyledTableRow component="div"
          data-testid={testid`shared-item-${accountItem._id}`}
          hover
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <StyledTableCell component="div"
            sx={{
              alignItems: 'center',
              display: 'flex',
              minWidth: 200,
              minHeight: 50,
              borderBottomWidth: filterAccounts.length === index + 1 ? 0 : 1,
            }}
          >
            <Link
              state={{ previousUrl: routerLocation.pathname }}
              style={{ flex: 1, textDecoration: 'none', color: 'initial' }}
              to={`/account/${accountItem._id}`}
            >
              {accountItem.display}
            </Link>
            {renderRemoveButton(accountItem)}
          </StyledTableCell>
        </StyledTableRow>
      )}
    />
  );

  if (accountIdsState === 'pending') return <MiddleSpinner />;
  if (accountIdsError) return <ErrorMessage error={accountIdsError} />;

  return (
    <BasicTableCard
      actionComponent={renderCreateAction()}
      hasShowAll
      headComponent={renderHead()}
      hintComponent={renderHint()}
      listComponent={filterAccounts.map((accountItem, i) => renderBody(accountItem, i))}
      titleComponent={renderTitle()}
    />
  );
};
